import { type DefaultError, keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { createAlbum, deleteAlbum, getAlbum, unDeleteAlbum } from '@/services/album.service';
import { invalidateAssetDataForNodeChange } from '@/hooks/websocket/mergeAssetPayload';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { type Album, type CreateAlbumParams } from '@/types/album';
import { ToastAction, ToastVariant } from '@/components/ui/toast';
import { useCommandContext } from '@/context/CommandContext';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useAlbum = (id?: string, options?: object) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { undo } = useCommandContext();

  const tErrors = useTranslations('errors');
  const tCommon = useTranslations('common');
  const tToasts = useTranslations('toasts');

  const getAlbumWithAuth = useAuthenticatedQueryFn(getAlbum);
  const authUnDeleteAlbum = useAuthenticatedQueryFn(unDeleteAlbum);
  const authDeleteAlbum = useAuthenticatedQueryFn(deleteAlbum);
  const createAlbumWithAuth = useAuthenticatedQueryFn(createAlbum);

  const {
    data: album,
    isLoading: isAlbumLoading,
    isPending: isAlbumPending,
    isFetching: isAlbumFetching,
    error: albumError,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.Album, id],
    queryFn: id ? () => getAlbumWithAuth(id) : () => undefined,
    placeholderData: keepPreviousData,
    enabled: false,
    ...options,
  });

  const { mutateAsync: createAlbumMutation } = useMutation<Album, DefaultError, CreateAlbumParams>({
    mutationFn: createAlbumWithAuth,
    onError: (err) => {
      errorLog('Action failed:', err);
      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tErrors('toastDefaultDescription'),
      });
    },
    onSuccess: (response) => {
      toast({
        title: tToasts('createAlbumMutation.title'),
        description: tToasts('createAlbumMutation.description'),
        action: (
          <ToastAction onClick={undo} altText={tToasts('createAlbumMutation.undoAlt')} icon={Undo2}>
            {tCommon('undo')}
          </ToastAction>
        ),
      });

      let path = [response.slug];
      if (response.ancestors && response.ancestors.length > 0) {
        const ancestorSlugs = response.ancestors?.map((ancestor) => ancestor.slug);
        path = [...ancestorSlugs, ...path];
      }
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.TreePath, path.join('/')],
        refetchType: 'all',
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
      void invalidateAssetDataForNodeChange(queryClient);

      return response;
    },
  });

  const { mutateAsync: unDeleteAlbumMutation } = useMutation<
    Album,
    DefaultError,
    Parameters<typeof unDeleteAlbum>['0']
  >({
    mutationFn: (params) => authUnDeleteAlbum(params),
    onSettled: () => {
      toast({
        title: tToasts('unDeleteAlbumMutation.title'),
        description: tToasts('unDeleteAlbumMutation.description'),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
      void invalidateAssetDataForNodeChange(queryClient);
    },
  });

  const { mutateAsync: deleteAlbumMutation } = useMutation<Album, DefaultError, Parameters<typeof deleteAlbum>['0']>({
    mutationFn: (params) => authDeleteAlbum(params),
    onSettled: () => {
      toast({
        title: tToasts('deleteAlbumMutation.title'),
        description: tToasts('deleteAlbumMutation.description'),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.Tree] });
      void invalidateAssetDataForNodeChange(queryClient);
    },
  });

  return {
    album,
    refetchAlbum: refetch,
    isAlbumFetching,
    isAlbumLoading,
    isAlbumPending,
    error: albumError,
    createAlbumMutation,
    unDeleteAlbumMutation,
    deleteAlbumMutation,
  };
};
